import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TrendingUp, Euro, Users, ShoppingBag, Download, Loader2, ArrowUpRight, Calendar,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminVenditeTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/vendite/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    window.open(`${API}/api/admin/vendite/export.csv`, "_blank");
  };

  if (loading) {
    return <div className="rounded-2xl border p-8 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>;
  }
  if (!stats) return <div className="text-red-500">Errore caricamento stats</div>;

  const maxTrend = Math.max(...(stats.trend_12_mesi || []).map(t => t.fatturato), 1);

  return (
    <div data-testid="admin-vendite" className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-emerald-700">Vendite & Fatture</div>
            <h2 className="text-2xl font-extrabold text-[#0a1f55]">Dashboard vendite</h2>
          </div>
        </div>
        <button onClick={exportCsv} data-testid="export-csv"
          className="h-10 px-4 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold text-[13px] inline-flex items-center gap-2">
          <Download className="h-4 w-4" /> Esporta CSV per commercialista
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi icon={Euro} label="Fatturato TOT" value={`€ ${stats.totale_generale.toFixed(2)}`} color="emerald" testid="kpi-total" />
        <Kpi icon={Euro} label="Anno corrente" value={`€ ${stats.totale_anno.toFixed(2)}`} color="blue" testid="kpi-year" />
        <Kpi icon={Euro} label="Mese corrente" value={`€ ${stats.totale_mese_corrente.toFixed(2)}`} color="amber" testid="kpi-month" />
        <Kpi icon={ShoppingBag} label="Transazioni" value={stats.transazioni_totali} color="violet" testid="kpi-tx" />
        <Kpi icon={Users} label="Clienti unici" value={stats.clienti_unici} color="indigo" testid="kpi-clients" />
      </div>

      {/* Trend 12 mesi */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-extrabold text-[#0a1f55]">Fatturato ultimi 12 mesi</h3>
          <div className="text-[11.5px] text-slate-500">Ticket medio: <strong className="text-[#0a1f55]">€ {stats.ticket_medio.toFixed(2)}</strong></div>
        </div>
        <div className="grid grid-cols-12 gap-1 items-end h-40">
          {stats.trend_12_mesi.map((t) => {
            const h = maxTrend > 0 ? Math.max(4, (t.fatturato / maxTrend) * 100) : 4;
            const [y, m] = t.mese.split("-");
            const monthLabel = ["","Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"][parseInt(m)];
            return (
              <div key={t.mese} className="flex flex-col items-center justify-end h-full group" title={`€ ${t.fatturato.toFixed(2)}`}>
                <div className="text-[9px] font-bold text-[#0a1f55] mb-1 opacity-0 group-hover:opacity-100 transition-opacity">€{Math.round(t.fatturato)}</div>
                <div className={`w-full rounded-t bg-gradient-to-t ${t.fatturato > 0 ? "from-[#102a6b] to-[#3b82f6]" : "from-slate-200 to-slate-100"}`}
                  style={{ height: `${h}%` }}
                />
                <div className="text-[10px] text-slate-500 mt-1">{monthLabel}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per pacchetto + Top clienti */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-extrabold text-[#0a1f55] mb-4">Per pacchetto</h3>
          <div className="space-y-2">
            {stats.per_pacchetto.length === 0 ? (
              <p className="text-slate-500 text-[13px]">Nessuna vendita registrata</p>
            ) : stats.per_pacchetto.map((p) => (
              <div key={p.package_id} data-testid={`pkg-${p.package_id}`} className="rounded-lg border border-slate-200 p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0a1f55] text-[13px] truncate">{p.nome}</div>
                  <div className="text-[11px] text-slate-500">{p.count} vendite</div>
                </div>
                <div className="font-extrabold text-emerald-700">€ {p.total.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-extrabold text-[#0a1f55] mb-4">Top 10 clienti</h3>
          <div className="space-y-2">
            {stats.top_clienti.length === 0 ? (
              <p className="text-slate-500 text-[13px]">Nessun cliente registrato</p>
            ) : stats.top_clienti.map((c, i) => (
              <div key={c.email} className="rounded-lg border border-slate-200 p-3 flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 font-extrabold text-[11px] flex items-center justify-center">#{i+1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0a1f55] text-[13px] truncate">{c.nome}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.email} · {c.count} acquisti</div>
                </div>
                <div className="font-extrabold text-emerald-700 text-[13px]">€ {c.total.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: I, label, value, color, testid }) {
  const colors = {
    emerald: "bg-emerald-500", blue: "bg-blue-500", amber: "bg-amber-500",
    violet: "bg-violet-500", indigo: "bg-indigo-500",
  };
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={`h-8 w-8 rounded-lg ${colors[color]} text-white flex items-center justify-center mb-2`}>
        <I className="h-4 w-4" />
      </div>
      <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-slate-500">{label}</div>
      <div className="text-lg font-extrabold text-[#0a1f55] mt-0.5">{value}</div>
    </div>
  );
}
